import { Component, HostListener, OnInit, ViewContainerRef, effect, inject, input, signal, viewChild, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';

import { NgSelectModule } from '@ng-select/ng-select';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { RegisterComponent } from '../../../components/component-lookup';
import { CanDeactivateComponent } from '../../../../shared/guards/pending-changes.guard';
import { GlobalConstants } from '../../../../shared/global-constants';
import { InstitutionAutocompleteComponent } from '../../../components/institution-autocomplete/institution-autocomplete.component';
import { InstitutionProfile, InstitutionProfileService, SimilarInstitutionsRequest, TargetedInstitutionsRequest } from '../../../services/ffiec/institution-profile.service';
import { CreateGroup, Group } from '../../../services/group.service';
import { DialogService } from '../../../../shared/services/dialog.service';
import { StateService } from '../../../services/state.service';
import { UserService } from '../../../../shared/services/user.service';
import { Institution } from '../../../services/institution.service';
import { DateSelectorComponent } from '../../../components/date-selector/date-selector.component';
import { CreateGroupDialogComponent, CreateGroupDialogResults } from '../../create-group-dialog/create-group-dialog.component';

enum PeerGroupType {
  None,
  Similar,
  Targeted,
  Distinct
}

@RegisterComponent('FfiecCreateGroup')
@Component({
  selector: 'app-ffiec-create-group',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxMaskDirective, NgSelectModule, NgxPaginationModule,
    InstitutionAutocompleteComponent, DateSelectorComponent],
  templateUrl: './create-group.component.html',
  styleUrl: './create-group.component.scss',
  providers: [provideNgxMask({ thousandSeparator: ',' })]
})
export class FfiecCreateGroupComponent implements OnInit, CanDeactivateComponent {
  institutionAutocompletes = viewChildren(InstitutionAutocompleteComponent);
  dialog = viewChild.required('dialog', { read: ViewContainerRef });

  paywall = input<boolean>();
  paywallMessage = input<string | null>();

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialogService = inject(DialogService);
  private profileService = inject(InstitutionProfileService);
  private stateService = inject(StateService);
  readonly groups = inject(StateService).groups;
  readonly institution = inject(StateService).institution;
  readonly user = inject(UserService).state;

  readonly PeerGroupType = PeerGroupType;
  selection: PeerGroupType = PeerGroupType.None;

  clientProfiles = signal<InstitutionProfile[] | undefined>(undefined);
  clientProfile = signal<InstitutionProfile | undefined>(undefined);

  selectedInstitution?: InstitutionProfile;
  institutions: InstitutionProfile[] = [];
  removedInstituions: InstitutionProfile[] = [];
  distinctInstitutions: InstitutionProfile[] = [];
  distinctIids: number[] = [];
  selectionCriteria = '';

  date = signal<Date | undefined>(undefined);
  states = GlobalConstants.states;
  selectedStates: string[] = [];
  ownershipTypes: string[] = [];

  filtersModified = false;

  assetRangeEnabled = true;
  assetRangeMask = 'separator';
  assetRangeMin?: number;
  assetRangeMax?: number;

  lnsToDepRangeEnabled = false;
  lnsToDepRangeMask = 'separator.2';
  lnsToDepRangeMin?: number;
  lnsToDepRangeMax?: number;

  assetGrowthRangeEnabled = false
  assetGrowthRangeMask = 'separator';
  assetGrowthRangeMin?: number;
  assetGrowthRangeMax?: number;

  agiInd = false;
  commlInd = false;
  creInd = false;
  resiInd = false;
  consumerInd = false;
  hfsInd = false;
  ccsvcInd = false;
  lsefinSvcInd = false;
  wealthSvcInd = false;
  insuranceInd = false;
  roeFilter = 0;
  roeMin?: number;
  roeMax?: number;
  roaFilter = 0;
  roaMin?: number;
  roaMax?: number;
  netIntMarginFilter = 0;
  netIntMarginMin?: number;
  netIntMarginMax?: number;
  netOverheadFilter = 0;
  netOverheadMin?: number;
  netOverheadMax?: number;
  yeaFilter = 0;
  yeaMin?: number;
  yeaMax?: number;
  yldOnLnsFilter = 0;
  yldOnLnsMin?: number;
  yldOnLnsMax?: number;
  cofFilter = 0;
  cofMin?: number;
  cofMax?: number;
  cstDepFilter = 0;
  cstDepMin?: number;
  cstDepMax?: number;
  texasRatioFilter = 0;
  texasRatioMin?: number;
  texasRatioMax?: number;

  sortColumn = 'name';
  sortDir = 'asc';
  page = 1;

  isDirty = false;
  similarMore = false;
  basicPlan = false;
  loading = false;

  institutionProfileExpanded = false;
  loanConcentrationExpanded = false;
  servicesOfferedExpanded = false;
  performanceMetricsExpanded = false;

  displayColumns = signal<boolean[]>([true, true, true, true, true, true, true, true, true, false, false, false]);

  @HostListener('window:beforeunload', ['$event'])
  onbeforeunload(event: { preventDefault: () => void; returnValue: boolean; }) {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  constructor() {
    effect(() => {
      const date = this.date();
      const clientProfiles = this.clientProfiles();
      if (date && clientProfiles) {
        const clientProfile = this.clientProfiles()?.find(i => new Date(i.reportDate).getTime() == date.getTime());
        if (clientProfile && !this.filtersModified) {
          this.assetRangeMin = Math.round((clientProfile.totalAssetsQe * .9) / 1000000);
          this.assetRangeMax = Math.round((clientProfile.totalAssetsQe * 1.1) / 1000000);
          this.lnsToDepRangeMin = Math.round(clientProfile.lnsToDep * .9);
          this.lnsToDepRangeMax = Math.round(clientProfile.lnsToDep * 1.1);
          this.assetGrowthRangeMin = 5;
          this.assetGrowthRangeMax = 10;
          if (clientProfile.ownershipType != undefined) {
            this.ownershipTypes = [clientProfile.ownershipType];
          }
        }
        setTimeout(() => {
          this.clientProfile.set(clientProfile);
          this.getInstitutions();
        });
      }
    });
  }

  ngOnInit() {
    this.profileService.getInstitutionProfiles(this.institution().iid).subscribe(profiles => {
      this.clientProfiles.set(profiles);
    });
  }

  switchType(type: PeerGroupType) {
    if (this.selection == PeerGroupType.None) {
      this.selection = type;
      this.updateUrl(type);
      if (this.selection == PeerGroupType.Distinct) {
        this.institutions = this.distinctInstitutions;
        this.selectionCriteria = 'Distinct banks manually selected by ' + this.user()?.displayName;
        const clientProfile = this.clientProfiles()![this.clientProfiles.length - 1];
        this.clientProfile.set(clientProfile);
      }
    }
  }

  updateUrl(type: PeerGroupType) {
    let param = '';
    switch (type) {
      case PeerGroupType.Similar: {
        param = 'similar';
        break;
      }
      case PeerGroupType.Targeted: {
        param = 'targeted';
        break;
      }
      case PeerGroupType.Distinct: {
        param = 'distinct';
        break;
      }
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: param
      },
      queryParamsHandling: 'merge',
      skipLocationChange: false
    });
  }

  onDateChange(date: Date) {
    this.date.set(date);
  }

  onFilterChange() {
    this.filtersModified = true;
    this.getInstitutions();
  }

  getInstitutions() {
    this.page = 1;
    switch (this.selection) {
      case PeerGroupType.Similar: {
        if (this.date == undefined) return;
        const similarModel: SimilarInstitutionsRequest = {
          reportDate: this.date()?.toISOString() ?? '',
          states: this.selectedStates,
          ownershipTypes: this.ownershipTypes
        }
        this.loading = true;
        this.profileService.getSimilarInstitutions(this.institution()!.iid, similarModel).subscribe(selection => {
          this.selectionCriteria = selection.selectionCriteria;
          this.institutions = selection.institutionProfiles.filter(i => this.removedInstituions.every(r => r.iid != i.iid));
          this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
          this.setDisplayColumns();
          this.loading = false;
        });
        break;
      }
      case PeerGroupType.Targeted: {
        if (this.date == undefined) return;
        if (this.selectedStates.length == 0
          && ((this.assetRangeEnabled == false) || this.assetRangeMin == undefined && this.assetRangeMax == undefined)
          && this.lnsToDepRangeEnabled == false
          && this.assetGrowthRangeEnabled == false && this.agiInd == false
          && this.commlInd == false && this.creInd == false && this.resiInd == false && this.consumerInd == false
          && this.hfsInd == false && this.ccsvcInd == false && this.lsefinSvcInd == false && this.wealthSvcInd == false
          && this.insuranceInd == false && this.roeFilter == 0 && this.roaFilter == 0 && this.netIntMarginFilter == 0
          && this.netOverheadFilter == 0 && this.yeaFilter == 0 && this.yldOnLnsFilter == 0 && this.cofFilter == 0
          && this.cstDepFilter == 0 && this.texasRatioFilter == 0) {
          this.institutions = [];
          break;
        }
        const targetedModel: TargetedInstitutionsRequest = {
          reportDate: this.date()?.toISOString() ?? '',
          states: this.selectedStates,
          ownershipTypes: this.ownershipTypes,
          useAssets: this.assetRangeEnabled,
          assetsMin: this.assetRangeMin ? this.assetRangeMin : undefined,
          assetsMax: this.assetRangeMax ? this.assetRangeMax : undefined,
          useLnsToDep: this.lnsToDepRangeEnabled,
          lnsToDepMin: this.lnsToDepRangeMin ? this.lnsToDepRangeMin : undefined,
          lnsToDepMax: this.lnsToDepRangeMax ? this.lnsToDepRangeMax : undefined,
          useAssetGrowth: this.assetGrowthRangeEnabled,
          assetGrowthMin: this.assetGrowthRangeMin ? this.assetGrowthRangeMin : undefined,
          assetGrowthMax: this.assetGrowthRangeMax ? this.assetGrowthRangeMax : undefined,
          agiInd: this.agiInd,
          commlInd: this.commlInd,
          creInd: this.creInd,
          resiInd: this.resiInd,
          consumerInd: this.consumerInd,
          hfsInd: this.hfsInd,
          ccsvcInd: this.ccsvcInd,
          lsefinSvcInd: this.lsefinSvcInd,
          wealthSvcInd: this.wealthSvcInd,
          insuranceInd: this.insuranceInd,
          roeFilter: this.roeFilter == -1 && !this.roeMin && !this.roeMax ? 0 : this.roeFilter,
          roeMin: this.roeMin != undefined && this.roeMin > 0 ? this.roeMin : undefined,
          roeMax: this.roeMax != undefined && this.roeMax > 0 ? this.roeMax : undefined,
          roaFilter: this.roaFilter == -1 && !this.roaMin && !this.roaMax ? 0 : this.roaFilter,
          roaMin: this.roaMin != undefined && this.roaMin > 0 ? this.roaMin : undefined,
          roaMax: this.roaMax != undefined && this.roaMax > 0 ? this.roaMax : undefined,
          netIntMarginFilter: this.netIntMarginFilter == -1 && !this.netIntMarginMin && !this.netIntMarginMax ? 0 : this.netIntMarginFilter,
          netIntMarginMin: this.netIntMarginMin != undefined && this.netIntMarginMin > 0 ? this.netIntMarginMin : undefined,
          netIntMarginMax: this.netIntMarginMax != undefined && this.netIntMarginMax > 0 ? this.netIntMarginMax : undefined,
          netOverheadFilter: this.netOverheadFilter == -1 && !this.netOverheadMin && !this.netOverheadMax ? 0 : this.netOverheadFilter,
          netOverheadMin: this.netOverheadMin != undefined && this.netOverheadMin > 0 ? this.netOverheadMin : undefined,
          netOverheadMax: this.netOverheadMax != undefined && this.netOverheadMax > 0 ? this.netOverheadMax : undefined,
          yeaFilter: this.yeaFilter == -1 && !this.yeaMin && !this.yeaMax ? 0 : this.yeaFilter,
          yeaMin: this.yeaMin != undefined && this.yeaMin > 0 ? this.yeaMin : undefined,
          yeaMax: this.yeaMax != undefined && this.yeaMax > 0 ? this.yeaMax : undefined,
          yldOnLnsFilter: this.yldOnLnsFilter == -1 && !this.yldOnLnsMin && !this.yldOnLnsMax ? 0 : this.yldOnLnsFilter,
          yldOnLnsMin: this.yldOnLnsMin != undefined && this.yldOnLnsMin > 0 ? this.yldOnLnsMin : undefined,
          yldOnLnsMax: this.yldOnLnsMax != undefined && this.yldOnLnsMax > 0 ? this.yldOnLnsMax : undefined,
          cofFilter: this.cofFilter == -1 && !this.cofMin && !this.cofMax ? 0 : this.cofFilter,
          cofMin: this.cofMin != undefined && this.cofMin > 0 ? this.cofMin : undefined,
          cofMax: this.cofMax != undefined && this.cofMax > 0 ? this.cofMax : undefined,
          cstDepFilter: this.cstDepFilter == -1 && !this.cstDepMin && !this.cstDepMax ? 0 : this.cstDepFilter,
          cstDepMin: this.cstDepMin != undefined && this.cstDepMin > 0 ? this.cstDepMin : undefined,
          cstDepMax: this.cstDepMax != undefined && this.cstDepMax > 0 ? this.cstDepMax : undefined,
          texasRatioFilter: this.texasRatioFilter == -1 && !this.texasRatioMin && !this.texasRatioMax ? 0 : this.texasRatioFilter,
          texasRatioMin: this.texasRatioMin != undefined && this.texasRatioMin > 0 ? this.texasRatioMin : undefined,
          texasRatioMax: this.texasRatioMax != undefined && this.texasRatioMax > 0 ? this.texasRatioMax : undefined
        }

        this.loading = true;
        this.profileService.getTargetedInstitutions(this.institution()!.iid, targetedModel).subscribe(selection => {
          this.selectionCriteria = selection.selectionCriteria;
          this.institutions = selection.institutionProfiles.filter(i => this.removedInstituions.every(r => r.iid != i.iid));
          this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
          this.setDisplayColumns();
          this.loading = false;
        });
        break;
      }
      case PeerGroupType.Distinct: {
        this.institutions = this.distinctInstitutions;
        this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
        break;
      }
    }
  }

  setDisplayColumns() {
    const displayColumns: boolean[] = [];
    displayColumns[0] = this.assetRangeEnabled;
    displayColumns[1] = this.lnsToDepRangeEnabled;
    displayColumns[2] = this.roaFilter != 0;
    displayColumns[3] = this.netIntMarginFilter != 0;
    displayColumns[4] = this.netOverheadFilter != 0;
    displayColumns[5] = this.yeaFilter != 0;
    displayColumns[6] = this.assetGrowthRangeEnabled;
    displayColumns[7] = this.roeFilter != 0;
    displayColumns[8] = this.texasRatioFilter != 0;
    displayColumns[9] = this.yldOnLnsFilter != 0;
    displayColumns[10] = this.cofFilter != 0;
    displayColumns[11] = this.cstDepFilter != 0;

    let count = displayColumns.filter(c => c).length;
    while (count < 9) {
      const index = displayColumns.findIndex(c => !c);
      displayColumns[index] = true;
      count++;
    }
    while (count > 9) {
      const index = displayColumns.lastIndexOf(true);
      displayColumns[index] = false;
      count--;
    }

    this.displayColumns.set(displayColumns);
  }

  onSelectInstitution(insitution: Institution) {
    this.profileService.getInstitutionProfile(insitution.iid).subscribe(profile => {
      this.selectedInstitution = profile;
      this.addInstitution();
    });
  }

  addInstitution() {
    this.institutionAutocompletes().forEach(autocomplete => autocomplete.clearInstitution());
    if (this.selectedInstitution) {
      this.selectedInstitution.isAdded = true;
      this.isDirty = true;
      this.distinctInstitutions.unshift(this.selectedInstitution);
      setTimeout(() => {
        if (this.selectedInstitution) {
          this.selectedInstitution.isAdded = false;
        }
      }, 3000);
      this.sortColumn = ''; this.sortDir = 'asc';
    }
  }

  removeInstitution(iid: number) {
    const institution = this.institutions.find(i => i.iid == iid) ?? {} as InstitutionProfile;
    institution.isRemoved = true;
    this.isDirty = true;
    setTimeout(() => {
      if (this.selection == PeerGroupType.Distinct) {
        institution.isRemoved = false;
        const index = this.distinctInstitutions.findIndex(i => i.iid == iid);
        this.distinctInstitutions.splice(index, 1);
      }
      else {
        institution.isRemoved = false;
        const index = this.institutions.findIndex(i => i.iid == iid);
        this.institutions.splice(index, 1);
        this.removedInstituions.push(institution);
      }
    }, 3000)
  }

  createGroup() {
    this.dialogService.show(this.dialog(), CreateGroupDialogComponent, { groupType: this.selection, numSelected: this.institutions.length })
      .subscribe((modalResult) => {
        if (modalResult.isSubmitted) {
          const results = modalResult.value as CreateGroupDialogResults;
          const group: CreateGroup = {
            name: results.name,
            description: results.description,
            criteria: this.selectionCriteria,
            iids: []
          }
          this.institutions.forEach(i => {
            group.iids.push(i.iid);
          });

          setTimeout(() => {
            this.saveGroup(group);
          }, 800);
        }
      });
    return false;
  }

  saveGroup(group: CreateGroup) {
    this.isDirty = false;
    this.stateService.createGroup(this.institution()!.iid, group as Group).subscribe(() => {
      this.router.navigate(['/peer-comparison/groups/manage']);
    });
  }

  discardChanges() {
    this.selectedStates = [];
    this.assetRangeEnabled = true;
    this.assetGrowthRangeEnabled = false;
    this.agiInd = false;
    this.commlInd = false;
    this.creInd = false;
    this.resiInd = false;
    this.consumerInd = false;
    this.hfsInd = false;
    this.ccsvcInd = false;
    this.lsefinSvcInd = false;
    this.wealthSvcInd = false;
    this.insuranceInd = false;
    this.roeFilter = 0;
    this.roaFilter = 0;
    this.netIntMarginFilter = 0;
    this.netOverheadFilter = 0;
    this.yeaFilter = 0;
    this.yldOnLnsFilter = 0;
    this.cofFilter = 0;
    this.cstDepFilter = 0;
    this.texasRatioFilter = 0;
    this.sortColumn = 'name';
    this.sortDir = 'asc';
    this.removedInstituions = [];
    this.distinctInstitutions = [];
    this.isDirty = false;
    this.selection = PeerGroupType.None;
    this.similarMore = false;
    this.selectionCriteria = '';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: null
      },
      queryParamsHandling: 'merge',
      skipLocationChange: false
    });
  }

  onSort(column: string) {
    if (this.sortColumn == column) {
      this.sortDir = this.sortDir == 'asc' ? 'desc' : 'asc';
    }
    else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
    this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dynamicSort(property: any, dir: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (a: any, b: any) {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return dir == 'asc' ? result : result * -1;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    return !(this.isDirty && this.institutions.length > 0);
  }

  usdDisplay(value: number | undefined): string {
    return value != undefined ? new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value / 1000000) + 'MM' : '';
  }

  pctDisplay(value: number | undefined): string {
    return value != undefined ? value.toFixed(2) + '%' : '';
  }

  fixUrl(url: string): string {
    return !/^https?:\/\//i.test(url) ? 'http://' + url : url;
  }
}
